import { useEffect, useMemo, useState } from "react";

import "./styles.css";

const PAGE_SIZE = 10;

/**
 * InvoicesTable – exibe faturas em uma tabela simples.
 * Espera um array:
 *   {
 *     id: string,
 *     createdAt: "YYYY-MM-DDTHH:mm:ss",
 *     amount: number, // em centavos
 *     status: "paid" | "unpaid" | "pending" | ...
 *     invoiceUrl?: string
 *   }
 */
const InvoicesTable = ({ invoices = [] }) => {
    const [page, setPage] = useState(1);

    const printAmount = (cents) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(cents / 100);

    const formatDate = (isoDate) => {
        if (!isoDate) return "-";
        const [date] = isoDate.split("T"); // separa a parte da data
        const [y, m, d] = date.split("-");
        return `${d}/${m}/${y}`;
    };

    const handleView = (invoice) => {
        const invoiceUrl = invoice.invoiceUrl;
        if (!invoiceUrl) return;
        window.open(invoiceUrl, "_blank", "noopener,noreferrer");
    };

    const sortedInvoices = useMemo(() => {
        return [...(invoices || [])].sort((a, b) => {
            const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
        });
    }, [invoices]);

    const totalPages = Math.max(1, Math.ceil(sortedInvoices.length / PAGE_SIZE));

    useEffect(() => {
        if (page > totalPages) setPage(1);
    }, [totalPages, page]);

    const pagedInvoices = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return sortedInvoices.slice(start, start + PAGE_SIZE);
    }, [sortedInvoices, page]);

    const goPrev = () => setPage((p) => Math.max(1, p - 1));
    const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

    return (
        <div className="invoices-table-wrapper">
            <table className="invoices-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Date</th>
                        <th className="text-right">Amount</th>
                        <th>Status</th>
                        <th className="text-center">Receipt</th>
                    </tr>
                </thead>
                <tbody>
                    {pagedInvoices.length === 0 ? (
                        <tr>
                            <td colSpan="5" className="no-data">
                                No invoices available
                            </td>
                        </tr>
                    ) : (
                        pagedInvoices.map((inv) => (
                            <tr key={inv.id}>
                                <td>{inv.id?.slice(0, 10) || "-"}</td>
                                <td>{formatDate(inv.createdAt)}</td>
                                <td className="text-right">{printAmount(inv.amount)}</td>
                                <td>
                                    <span className={`status-badge ${inv.status}`}>
                                        {inv.status}
                                    </span>
                                </td>
                                <td className="text-center">
                                    <button className="view-btn" onClick={() => handleView(inv)}>
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {sortedInvoices.length > 0 && (
                <div className="invoices-pagination">
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={goPrev}
                        disabled={page === 1}
                    >
                        Previous
                    </button>
                    <span className="pagination-info">
                        Page {page} of {totalPages}
                    </span>
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={goNext}
                        disabled={page === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default InvoicesTable;
